import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Minus, ChevronUp, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * "Temporada" panel: a compact table of all matches with W/D/L icons,
 * cumulative running stats and per-team filter.
 */
export default function SeasonView({ teams, selectedTeamId }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = selectedTeamId && selectedTeamId !== "all" ? `?team_id=${selectedTeamId}` : "";
        const r = await api.get(`/matches${params}`);
        // Sort newest first
        const sorted = [...(r.data || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setMatches(sorted);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedTeamId]);

  const teamById = useMemo(
    () => Object.fromEntries((teams || []).map((t) => [t.id, t])),
    [teams],
  );

  // Parse "X-Y" result string with regard to home/away orientation to classify
  // the match as Win/Draw/Loss for the club's team.
  const classify = (m) => {
    const raw = (m.result || "").trim().replace("–", "-");
    const parts = raw.split("-").map((s) => s.trim());
    if (parts.length !== 2 || !/^\d+$/.test(parts[0]) || !/^\d+$/.test(parts[1])) {
      return { outcome: null, gf: null, ga: null };
    }
    const a = parseInt(parts[0], 10);
    const b = parseInt(parts[1], 10);
    const isHome = m.home_away === "casa";
    const gf = isHome ? a : b;
    const ga = isHome ? b : a;
    const outcome = gf > ga ? "W" : gf < ga ? "L" : "D";
    return { outcome, gf, ga };
  };

  // Cumulative season stats across all listed matches.
  const stats = useMemo(() => {
    let w = 0, d = 0, l = 0, gf = 0, ga = 0, played = 0;
    matches.forEach((m) => {
      const c = classify(m);
      if (!c.outcome) return;
      played += 1;
      if (c.outcome === "W") w += 1;
      else if (c.outcome === "D") d += 1;
      else l += 1;
      gf += c.gf || 0;
      ga += c.ga || 0;
    });
    const points = w * 3 + d;
    return { played, w, d, l, gf, ga, points, diff: gf - ga };
  }, [matches]);

  // Last 10 results, oldest → newest, only those with a valid score.
  const last10 = useMemo(() => {
    const withScores = matches
      .map((m) => ({ m, c: classify(m) }))
      .filter(({ c }) => c.outcome !== null);
    // matches is newest-first; take the first 10 then reverse to chronological order
    return withScores.slice(0, 10).reverse();
  }, [matches]);

  const OutcomeBadge = ({ outcome }) => {
    if (!outcome) return <span className="text-slate-300 text-xs">—</span>;
    const map = {
      W: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "V", Icon: ChevronUp },
      D: { bg: "bg-amber-100", fg: "text-amber-700", label: "E", Icon: Minus },
      L: { bg: "bg-rose-100", fg: "text-rose-700", label: "D", Icon: ChevronDown },
    }[outcome];
    return (
      <span
        className={`inline-flex items-center justify-center gap-0.5 w-7 h-7 rounded-full font-black text-xs ${map.bg} ${map.fg}`}
        data-testid={`season-outcome-${outcome}`}
      >
        <map.Icon className="h-3 w-3" /> {map.label}
      </span>
    );
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="season-view">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="h-4 w-4 text-purple-600" /> Temporada
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Last-10 trend sparkline */}
        {last10.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                Últimos {last10.length} resultados
              </span>
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> V</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> E</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> D</span>
              </div>
            </div>
            <Sparkline points={last10} />
          </div>
        )}

        {/* Aggregate strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
          {[
            { k: "PJ", v: stats.played, color: "text-slate-700" },
            { k: "V", v: stats.w, color: "text-emerald-700" },
            { k: "E", v: stats.d, color: "text-amber-700" },
            { k: "D", v: stats.l, color: "text-rose-700" },
            { k: "GF", v: stats.gf, color: "text-purple-700" },
            { k: "GC", v: stats.ga, color: "text-slate-600" },
            { k: "PTS", v: stats.points, color: "text-purple-700 font-black" },
          ].map((s) => (
            <div key={s.k} className="text-center bg-slate-50 rounded-md py-2">
              <div className={`text-2xl font-black ${s.color}`} style={{ fontFamily: "Nunito" }}>
                {s.v}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{s.k}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="py-6 text-sm text-slate-500 text-center">Cargando partidos...</div>
        ) : matches.length === 0 ? (
          <div className="py-6 text-sm text-slate-400 italic text-center">No hay partidos registrados.</div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-2 text-left font-bold">Fecha</th>
                  <th className="px-2 py-2 text-left font-bold">Equipo</th>
                  <th className="px-2 py-2 text-left font-bold">vs Rival</th>
                  <th className="px-2 py-2 text-center font-bold">L/V</th>
                  <th className="px-2 py-2 text-center font-bold">Resultado</th>
                  <th className="px-2 py-2 text-center font-bold">Goleadores</th>
                  <th className="px-2 py-2 text-center font-bold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((m) => {
                  const c = classify(m);
                  const team = teamById[m.team_id];
                  return (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-purple-50/40">
                      <td className="px-2 py-2 text-slate-700 font-mono">{m.date}</td>
                      <td className="px-2 py-2 text-slate-700">
                        {team?.name || "—"}
                        <span className="ml-1 text-[10px] text-slate-400">{team?.category}</span>
                      </td>
                      <td className="px-2 py-2">
                        <Link
                          to={`/matches/${m.id}`}
                          className="text-slate-800 font-bold hover:text-purple-700"
                          data-testid={`season-link-${m.id}`}
                        >
                          {m.rival}
                        </Link>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                          m.home_away === "casa" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"
                        }`}>
                          {m.home_away === "casa" ? "Local" : "Visit."}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center font-black text-slate-800">
                        {m.result || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-[11px] text-slate-600 max-w-[180px] truncate" title={m.goal_scorers}>
                        {m.goal_scorers || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <OutcomeBadge outcome={c.outcome} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Mini line chart: maps each match to a Y position (top=Win, middle=Draw,
 * bottom=Loss) connected by a thin grey line and a coloured dot per match.
 */
function Sparkline({ points }) {
  const W = 320;
  const H = 64;
  const padX = 14;
  const innerW = W - padX * 2;
  const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;
  // Win=top, Draw=middle, Loss=bottom
  const yFor = (outcome) => (outcome === "W" ? 12 : outcome === "L" ? H - 12 : H / 2);
  const colorFor = (outcome) =>
    outcome === "W" ? "#10B981" : outcome === "L" ? "#F43F5E" : "#F59E0B";

  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${padX + i * stepX} ${yFor(p.c.outcome)}`)
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      style={{ aspectRatio: `${W} / ${H}` }}
      data-testid="season-sparkline"
    >
      {/* Reference lines for W, D, L */}
      <line x1={padX} x2={W - padX} y1={12} y2={12} stroke="#E2E8F0" strokeWidth="1" strokeDasharray="2 3" />
      <line x1={padX} x2={W - padX} y1={H / 2} y2={H / 2} stroke="#E2E8F0" strokeWidth="1" strokeDasharray="2 3" />
      <line x1={padX} x2={W - padX} y1={H - 12} y2={H - 12} stroke="#E2E8F0" strokeWidth="1" strokeDasharray="2 3" />
      {/* Trend line */}
      {points.length > 1 && (
        <path d={pathD} stroke="#CBD5E1" strokeWidth="1.5" fill="none" />
      )}
      {/* Points */}
      {points.map((p, i) => (
        <g key={p.m.id}>
          <title>{`${p.m.date} · vs ${p.m.rival} · ${p.m.result || "—"}`}</title>
          <circle
            cx={padX + i * stepX}
            cy={yFor(p.c.outcome)}
            r={5}
            fill={colorFor(p.c.outcome)}
            stroke="#fff"
            strokeWidth="1.5"
          />
        </g>
      ))}
    </svg>
  );
}
